import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { useAuthStore } from '@/stores/auth-store';
import type {
  CompanyPayment,
  CompanyLimitation,
  SubscriptionAddOnStatus,
} from '@scio3/shared-types';
import { GET_SUB_ACCOUNT_INFO, GET_TEAM_BILLING_INFO } from '@scio3/graphql-client';
import { getCurrencySymbol } from '../utils/plan-helpers';

type UseBillingDataOptions = {
  /** When provided, fetches fresh sub-account company data instead of reading from auth store */
  subAccountId?: string;
  /** When provided, fetches fresh team billing data from server */
  teamId?: string;
  /** Skip the sub-account/team query (e.g., when dialog is closed) */
  skip?: boolean;
};

/**
 * Single source of truth for billing state.
 *
 * - No subAccountId/teamId → reads from auth store (logged-in user's company, instant, no network)
 * - With subAccountId → queries GET_SUB_ACCOUNT_INFO fresh from server (network-only)
 * - With teamId → queries GET_TEAM_BILLING_INFO fresh from server (network-only)
 *
 * All billing dialogs/pages should use this hook with the appropriate context.
 */
export function useBillingData(options?: UseBillingDataOptions) {
  const subAccountId = options?.subAccountId;
  const teamId = options?.teamId;
  const skip = options?.skip ?? false;

  // Always read parent data from auth store (instant, no query)
  const company = useAuthStore((s) => s.company);
  const me = useAuthStore((s) => s.me);

  // For sub-accounts: fetch fresh company data from server
  const { data: subInfoData, loading: loadingSubAccount } = useQuery(GET_SUB_ACCOUNT_INFO, {
    variables: { subAccountId: subAccountId ?? '' },
    fetchPolicy: 'network-only',
    skip: !subAccountId || skip,
  });

  // For team billing: fetch fresh team data from server
  const { data: teamInfoData, loading: loadingTeam } = useQuery(GET_TEAM_BILLING_INFO, {
    variables: { teamId: teamId ?? '' },
    fetchPolicy: 'network-only',
    skip: !teamId || skip,
  });

  const subCompany = useMemo(() => {
    if (!subAccountId || !subInfoData) return null;
    const response = (subInfoData as Record<string, unknown>)?.getSubAccountInfo as
      | { status?: boolean; company?: Record<string, unknown> }
      | undefined;
    return response?.company ?? null;
  }, [subAccountId, subInfoData]);

  const teamData = useMemo(() => {
    if (!teamId || !teamInfoData) return null;
    const response = (teamInfoData as Record<string, unknown>)?.getTeamBillingInfo as
      | { status?: boolean; team?: Record<string, unknown> }
      | undefined;
    return response?.team ?? null;
  }, [teamId, teamInfoData]);

  // Determine data source: sub-account, team billing, or parent (auth store)
  const isSubContext = !!subAccountId && !!subCompany;
  const isTeamContext = !!teamId && !!teamData;

  const subPay = subCompany?.payment as Record<string, unknown> | undefined;
  const subLim = subCompany?.limitation as Record<string, unknown> | undefined;
  const subCard = subCompany?.card as Record<string, unknown> | undefined;

  const teamPay = teamData?.payment as Record<string, unknown> | undefined;
  const teamLim = teamData?.limitation as Record<string, unknown> | undefined;
  const teamCard = teamData?.card as Record<string, unknown> | undefined;

  // --- Derive all billing fields from the appropriate source ---

  // Guard against the race-condition fallback: when a consumer requests
  // `{ subAccountId }` or `{ teamId }` but the corresponding query hasn't
  // resolved yet, `isSubContext`/`isTeamContext` are both false. Without this
  // guard we'd silently fall through to `company?.payment` / `company?.limitation`
  // — i.e. the PARENT account's values. That made the SubscriptionWizard
  // `Update Subscription` dialog initialise screenCount from the parent's
  // total screen count (Issue #49 — "shows 305 instead of 3 for team"). Now
  // we return `undefined` until the sub-account / team data loads, which
  // downstream resolves to `maxScreens=0` and keeps the wizard init effect
  // waiting (its guard `maxScreens <= 0` short-circuits until real data lands).
  const waitingForScopedData = (!!subAccountId && !isSubContext) || (!!teamId && !isTeamContext);

  const payment: CompanyPayment | undefined = isSubContext
    ? (subPay as unknown as CompanyPayment)
    : isTeamContext
      ? (teamPay as unknown as CompanyPayment)
      : waitingForScopedData
        ? undefined
        : company?.payment;

  const limitation: CompanyLimitation | undefined = isSubContext
    ? (subLim as unknown as CompanyLimitation)
    : isTeamContext
      ? (teamLim as unknown as CompanyLimitation)
      : waitingForScopedData
        ? undefined
        : company?.limitation;

  const activePay = isSubContext ? subPay : isTeamContext ? teamPay : undefined;

  const isTrial = activePay ? !!activePay.isTrial : (payment?.isTrial ?? false);
  const isActive = activePay ? !!activePay.isActive : (payment?.isActive ?? false);
  const planFeature = activePay
    ? ((activePay.planFeature as string) ?? 'free')
    : (payment?.planFeature ?? 'free');
  const trialEndDate = activePay
    ? (activePay.trialEndDate as string | undefined)
    : payment?.trialEndDate;
  const nextCharge = activePay ? undefined : payment?.nextCharge;
  const billingEmail = activePay
    ? (activePay.billing_email as string | undefined)
    : payment?.billing_email;
  const addons: SubscriptionAddOnStatus[] = activePay
    ? ((activePay.subscriptionIdAddOns ?? []) as SubscriptionAddOnStatus[])
    : (payment?.subscriptionIdAddOns ?? []);

  const activeLim = isSubContext ? subLim : isTeamContext ? teamLim : undefined;

  const maxScreens = activeLim
    ? ((activeLim.maxQuantity as number) ?? 0)
    : (limitation?.maxQuantity ?? 0);
  const usedScreens = activeLim
    ? ((activeLim.quantity as number) ?? 0)
    : (limitation?.quantity ?? 0);
  const maxStorage = activeLim
    ? ((activeLim.maxStorage as number) ?? 0)
    : (limitation?.maxStorage ?? 0);
  const usedStorage = activeLim
    ? ((activeLim.usedStorage as number) ?? 0)
    : (limitation?.usedStorage ?? 0);

  const isSubAccount = !!company?.partner?.name;
  const canChangeSub = !(
    'customerCanNotChangeSub' in (limitation ?? {}) &&
    (limitation as unknown as Record<string, unknown>)?.customerCanNotChangeSub
  );

  const currency = activePay
    ? ((activePay.currency as string) ?? 'usd')
    : (((company?.payment as Record<string, unknown> | undefined)?.currency as string) ?? 'usd');
  const currencySymbol = getCurrencySymbol(currency);

  const existingCard = useMemo(() => {
    const card = isSubContext
      ? subCard
      : isTeamContext
        ? teamCard
        : ((company as unknown as Record<string, unknown> | undefined)?.card as
            | Record<string, unknown>
            | undefined);
    // Recognize both card (has last4) and bank payment methods (iDEAL/SEPA/Bancontact — has paymentMethod)
    if (!card?.last4 && !card?.paymentMethod) return null;
    return {
      brand: (card.brand as string) ?? (card.paymentMethod as string) ?? 'Card',
      last4: (card.last4 as string) ?? '',
      exp_month: card.exp_month as number | undefined,
      exp_year: card.exp_year as number | undefined,
      type: (card.type as string) ?? 'card',
      paymentMethodId: (card.paymentMethodId as string) ?? 'card',
      paymentMethod: (card.paymentMethod as string) ?? '',
      address_country: (card.address_country as string) ?? '',
      address_zip: (card.address_zip as string) ?? '',
      address_city: (card.address_city as string) ?? '',
      address_state: (card.address_state as string) ?? '',
    };
  }, [isSubContext, isTeamContext, subCard, teamCard, company]);

  const defaultEmail =
    billingEmail ?? ((me as unknown as Record<string, unknown> | undefined)?.email as string) ?? '';

  // Team-specific fields — exposed when querying with teamId so the
  // Subscription wizard can render Team Name / Purpose inputs alongside the
  // screen counter (scio1 parity, Issue #49). Undefined for non-team contexts.
  const teamName = isTeamContext ? ((teamData?.name as string | undefined) ?? '') : undefined;
  const teamDescription = isTeamContext
    ? ((teamData?.description as string | undefined) ?? '')
    : undefined;

  return {
    payment,
    limitation,
    isTrial,
    isActive,
    planFeature,
    trialEndDate,
    nextCharge,
    billingEmail,
    addons,
    maxScreens,
    usedScreens,
    maxStorage,
    usedStorage,
    isSubAccount,
    canChangeSub,
    currency,
    currencySymbol,
    existingCard,
    defaultEmail,
    me,
    company,
    /** True while sub-account or team data is being fetched */
    loadingSubAccount: loadingSubAccount || loadingTeam,
    /** The raw sub-account company object (for passing to child components) */
    subCompanyData: subCompany,
    /** Team billing context — passed through to billing queries */
    teamId,
    /** Team display fields — populated only when `teamId` query resolves */
    teamName,
    teamDescription,
  };
}
